import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-ES');
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(num);
};

const HostingsPage = () => {
  const token = localStorage.getItem('token');
  const [hostings, setHostings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', clientId: '' });
  const [clientOptions, setClientOptions] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hostingForm, setHostingForm] = useState({
    id: null,
    clientId: '',
    hosting: '',
    caracteristicas: '',
    fechaAlta: '',
    fechaRenovacion: '',
    precio: ''
  });

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  }, []);

  const fetchHostings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.search) params.append('search', filters.search);
      if (filters.clientId) params.append('clientId', filters.clientId);
      const res = await apiFetch(`/api/hostings/all?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo hostings');
      setHostings(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, token, handleUnauthorized]);

  const fetchClients = useCallback(async (search = '') => {
    setClientsLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/clients?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      const options = (data?.data || []).map((c) => ({
        value: c.id,
        label: `${c.razon_social || `Cliente #${c.id}`}${c.email ? ` · ${c.email}` : ''}`,
        data: c
      }));
      setClientOptions(options);
    } catch (err) {
      console.error(err.message);
    } finally {
      setClientsLoading(false);
    }
  }, [token, handleUnauthorized]);

  const selectedClient = useMemo(() => {
    if (!filters.clientId) return null;
    const parsed = Number(filters.clientId);
    return clientOptions.find((opt) => opt.value === parsed)
      || { value: parsed, label: `Cliente #${filters.clientId}` };
  }, [filters.clientId, clientOptions]);

  const selectedHostingClient = useMemo(() => {
    if (!hostingForm.clientId) return null;
    const parsed = Number(hostingForm.clientId);
    return clientOptions.find((opt) => opt.value === parsed)
      || { value: parsed, label: `Cliente #${hostingForm.clientId}` };
  }, [hostingForm.clientId, clientOptions]);

  const openCreateModal = () => {
    setHostingForm({ id: null, clientId: '', hosting: '', caracteristicas: '', fechaAlta: '', fechaRenovacion: '', precio: '' });
    setModalOpen(true);
  };

  const openEditModal = (hosting) => {
    setHostingForm({
      id: hosting.id,
      clientId: hosting.client_id || '',
      hosting: hosting.hosting || '',
      caracteristicas: hosting.caracteristicas || '',
      fechaAlta: hosting.fecha_alta ? hosting.fecha_alta.slice(0, 10) : '',
      fechaRenovacion: hosting.fecha_renovacion ? hosting.fecha_renovacion.slice(0, 10) : '',
      precio: hosting.precio ?? ''
    });
    setModalOpen(true);
  };

  const submitHosting = async (e) => {
    e.preventDefault();
    if (!hostingForm.hosting) return alert('Hosting obligatorio');
    if (!hostingForm.id && !hostingForm.clientId) return alert('Selecciona un cliente');
    setSaving(true);
    try {
      const isEdit = Boolean(hostingForm.id);
      const url = isEdit
        ? `/api/clients/hostings/${hostingForm.id}`
        : `/api/clients/${hostingForm.clientId}/hostings`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          hosting: hostingForm.hosting,
          caracteristicas: hostingForm.caracteristicas || null,
          fechaAlta: hostingForm.fechaAlta || null,
          fechaRenovacion: hostingForm.fechaRenovacion || null,
          precio: hostingForm.precio || null
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error guardando hosting');
      setModalOpen(false);
      fetchHostings();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteHosting = async (hostingId) => {
    if (!window.confirm('¿Eliminar este hosting?')) return;
    try {
      const res = await apiFetch(`/api/clients/hostings/${hostingId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando hosting');
      fetchHostings();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    fetchHostings();
  }, [fetchHostings]);

  useEffect(() => {
    const query = clientQuery.trim();
    const handler = setTimeout(() => {
      fetchClients(query);
    }, 250);
    return () => clearTimeout(handler);
  }, [clientQuery, fetchClients]);

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Hosting</h2>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-primary btn-sm" onClick={openCreateModal}>Nuevo hosting</button>
          {loading && <span className="badge bg-info text-dark">Cargando...</span>}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-5">
              <input
                className="form-control form-control-sm"
                placeholder="Buscar hosting"
                value={filters.search}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, search: e.target.value })); }}
              />
            </div>
            <div className="col-md-5">
              <Select
                classNamePrefix="select"
                isLoading={clientsLoading}
                options={clientOptions}
                value={selectedClient}
                placeholder="Filtrar por cliente"
                onChange={(opt) => { setPage(1); setFilters((p) => ({ ...p, clientId: opt ? String(opt.value) : '' })); }}
                onInputChange={(val, action) => {
                  if (action.action === 'input-change') setClientQuery(val);
                  return val;
                }}
                isClearable
                noOptionsMessage={() => 'Sin resultados'}
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                type="button"
                onClick={() => { setFilters({ search: '', clientId: '' }); setPage(1); }}
              >
                Limpiar
              </button>
            </div>
          </div>

          {loading ? (
            <p>Cargando...</p>
          ) : hostings.length ? (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Hosting</th>
                    <th>Características</th>
                    <th>Alta</th>
                    <th>Renovación</th>
                    <th>Precio</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {hostings.map((h) => (
                    <tr key={h.id}>
                      <td>
                        {h.client_id ? (
                          <Link to={`/clientes/${h.client_id}`} className="text-decoration-none">
                            {h.razon_social || `Cliente #${h.client_id}`}
                          </Link>
                        ) : (h.razon_social || '-')}
                      </td>
                      <td>{h.hosting}</td>
                      <td>{h.caracteristicas || '-'}</td>
                      <td>{formatDate(h.fecha_alta)}</td>
                      <td>{formatDate(h.fecha_renovacion)}</td>
                      <td>{formatCurrency(h.precio)}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => openEditModal(h)}>Editar</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteHosting(h.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No hay hostings.</p>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{hostingForm.id ? 'Editar hosting' : 'Nuevo hosting'}</h5>
                <button className="btn-close" onClick={() => setModalOpen(false)}></button>
              </div>
              <form onSubmit={submitHosting}>
                <div className="modal-body">
                  {!hostingForm.id && (
                    <div className="mb-3">
                      <label className="form-label small">Cliente *</label>
                      <Select
                        classNamePrefix="select"
                        isLoading={clientsLoading}
                        options={clientOptions}
                        value={selectedHostingClient}
                        placeholder="Selecciona cliente"
                        onChange={(opt) => setHostingForm((p) => ({ ...p, clientId: opt ? String(opt.value) : '' }))}
                        onInputChange={(val, action) => {
                          if (action.action === 'input-change') setClientQuery(val);
                          return val;
                        }}
                        noOptionsMessage={() => 'Sin resultados'}
                      />
                    </div>
                  )}
                  {hostingForm.id && (
                    <div className="mb-3">
                      <label className="form-label small">Cliente</label>
                      <input className="form-control" value={selectedHostingClient?.label || '-'} disabled />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label small">Hosting *</label>
                    <input className="form-control" value={hostingForm.hosting} onChange={(e) => setHostingForm((p) => ({ ...p, hosting: e.target.value }))} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small">Características</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={hostingForm.caracteristicas}
                      onChange={(e) => setHostingForm((p) => ({ ...p, caracteristicas: e.target.value }))}
                    />
                  </div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small">Fecha alta</label>
                      <input className="form-control" type="date" value={hostingForm.fechaAlta} onChange={(e) => setHostingForm((p) => ({ ...p, fechaAlta: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Renovación</label>
                      <input className="form-control" type="date" value={hostingForm.fechaRenovacion} onChange={(e) => setHostingForm((p) => ({ ...p, fechaRenovacion: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Precio</label>
                      <input className="form-control" type="number" step="0.01" value={hostingForm.precio} onChange={(e) => setHostingForm((p) => ({ ...p, precio: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostingsPage;
